/**
 * Badge Component
 *
 * Displays status badges for translations with various variants and sizes.
 *
 * @package MultilingualPressZone
 * @since 1.0.0
 */

/**
 * Badge Component Class
 *
 * Creates and manages badge elements with support for:
 * - Multiple variants (success, warning, error, info, default)
 * - Size options (small, medium, large)
 * - Optional icons
 * - Pill and square styles
 * - Tooltips on hover
 */
class Badge {
    /**
     * Constructor
     *
     * @param {Object} options - Badge configuration
     * @param {string} options.text - Badge text content
     * @param {string} options.variant - Badge variant: 'default'|'primary'|'success'|'warning'|'danger'|'info'
     * @param {string} options.size - Badge size: 'small'|'medium'|'large'
     * @param {string} options.icon - Optional icon class (e.g., 'dashicons-yes')
     * @param {boolean} options.pill - Use pill (rounded) style
     * @param {boolean} options.dot - Show colored dot indicator
     * @param {boolean} options.removable - Show remove button (X)
     * @param {Function} options.onRemove - Callback when remove button is clicked
     * @param {string} options.tooltip - Optional tooltip text
     */
    constructor(options = {}) {
        // Validate and set defaults
        this.options = {
            text: options.text || '',
            variant: this.validateVariant(options.variant),
            size: this.validateSize(options.size),
            icon: options.icon || null,
            pill: options.pill !== undefined ? Boolean(options.pill) : false,
            dot: options.dot !== undefined ? Boolean(options.dot) : false,
            removable: options.removable !== undefined ? Boolean(options.removable) : false,
            onRemove: options.onRemove || null,
            tooltip: options.tooltip || null
        };

        // Create the badge element
        this.element = this.createBadge();

        // Set up remove button event listener if needed
        if (this.options.removable && this.options.onRemove) {
            this.setupRemoveListener();
        }
    }

    /**
     * Validate variant option
     *
     * @param {string} variant - Variant to validate
     * @return {string} Valid variant
     */
    validateVariant(variant) {
        const validVariants = ['default', 'primary', 'success', 'warning', 'danger', 'info'];

        // Map 'error' to 'danger' for backwards compatibility
        if (variant === 'error') {
            return 'danger';
        }

        return validVariants.includes(variant) ? variant : 'default';
    }

    /**
     * Validate size option
     *
     * @param {string} size - Size to validate
     * @return {string} Valid size
     */
    validateSize(size) {
        const validSizes = ['small', 'medium', 'large'];
        return validSizes.includes(size) ? size : 'medium';
    }

    /**
     * Create badge element
     *
     * @return {HTMLElement} Badge element
     */
    createBadge() {
        const badge = document.createElement('span');
        badge.className = this.buildClassNames();
        badge.setAttribute('role', 'status');
        badge.setAttribute('aria-label', this.options.text);

        // Add tooltip if provided
        if (this.options.tooltip) {
            badge.setAttribute('title', this.options.tooltip);
            badge.setAttribute('data-tooltip', this.options.tooltip);
        }

        // Add dot indicator if enabled
        if (this.options.dot) {
            const dot = this.createDot();
            badge.appendChild(dot);
        }

        // Add icon if provided
        if (this.options.icon) {
            const icon = this.createIcon();
            badge.appendChild(icon);
        }

        // Add text content
        const textNode = document.createTextNode(this.options.text);
        badge.appendChild(textNode);

        // Add remove button if enabled
        if (this.options.removable) {
            const removeBtn = this.createRemoveButton();
            badge.appendChild(removeBtn);
        }

        return badge;
    }

    /**
     * Build CSS class names for badge
     *
     * @return {string} Space-separated class names
     */
    buildClassNames() {
        const classes = ['mpz-badge'];

        // Add variant class
        classes.push(`mpz-badge--${this.options.variant}`);

        // Add size class
        classes.push(`mpz-badge--${this.options.size}`);

        // Add pill modifier if enabled
        if (this.options.pill) {
            classes.push('mpz-badge--pill');
        }

        // Add dot modifier if enabled
        if (this.options.dot) {
            classes.push('mpz-badge--with-dot');
        }

        // Add icon modifier if icon is present
        if (this.options.icon) {
            classes.push('mpz-badge--with-icon');
        }

        // Add removable modifier if enabled
        if (this.options.removable) {
            classes.push('mpz-badge--removable');
        }

        return classes.join(' ');
    }

    /**
     * Create dot indicator element
     *
     * @return {HTMLElement} Dot element
     */
    createDot() {
        const dot = document.createElement('span');
        dot.className = 'mpz-badge__dot';
        dot.setAttribute('aria-hidden', 'true');
        return dot;
    }

    /**
     * Create icon element
     *
     * @return {HTMLElement} Icon element
     */
    createIcon() {
        const icon = document.createElement('span');
        icon.className = `mpz-badge__icon dashicons ${this.options.icon}`;
        icon.setAttribute('aria-hidden', 'true');
        return icon;
    }

    /**
     * Create remove button element
     *
     * @return {HTMLElement} Remove button element
     */
    createRemoveButton() {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mpz-badge__remove';
        button.setAttribute('aria-label', 'Remove badge');
        button.textContent = '×';
        return button;
    }

    /**
     * Set up event listener for remove button
     */
    setupRemoveListener() {
        const removeBtn = this.element.querySelector('.mpz-badge__remove');
        if (removeBtn) {
            removeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();

                if (this.options.onRemove) {
                    this.options.onRemove(this);
                }
            });
        }
    }

    /**
     * Get the badge DOM element
     *
     * @return {HTMLElement} Badge element
     */
    getElement() {
        return this.element;
    }

    /**
     * Update badge text
     *
     * @param {string} text - New text content
     */
    setText(text) {
        this.options.text = text;

        // Update text node (keep icon if present)
        const textNodes = Array.from(this.element.childNodes).filter(
            node => node.nodeType === Node.TEXT_NODE
        );

        if (textNodes.length > 0) {
            textNodes[0].textContent = text;
        } else {
            this.element.appendChild(document.createTextNode(text));
        }

        // Update aria-label
        this.element.setAttribute('aria-label', text);
    }

    /**
     * Update badge variant
     *
     * @param {string} variant - New variant
     */
    setVariant(variant) {
        const oldVariant = this.options.variant;
        const newVariant = this.validateVariant(variant);

        if (oldVariant !== newVariant) {
            this.options.variant = newVariant;
            this.element.classList.remove(`mpz-badge--${oldVariant}`);
            this.element.classList.add(`mpz-badge--${newVariant}`);
        }
    }

    /**
     * Update badge size
     *
     * @param {string} size - New size
     */
    setSize(size) {
        const oldSize = this.options.size;
        const newSize = this.validateSize(size);

        if (oldSize !== newSize) {
            this.options.size = newSize;
            this.element.classList.remove(`mpz-badge--${oldSize}`);
            this.element.classList.add(`mpz-badge--${newSize}`);
        }
    }

    /**
     * Update tooltip
     *
     * @param {string|null} tooltip - New tooltip text (null to remove)
     */
    setTooltip(tooltip) {
        this.options.tooltip = tooltip;

        if (tooltip) {
            this.element.setAttribute('title', tooltip);
            this.element.setAttribute('data-tooltip', tooltip);
        } else {
            this.element.removeAttribute('title');
            this.element.removeAttribute('data-tooltip');
        }
    }

    /**
     * Destroy the badge
     * Removes event listeners and DOM element
     */
    destroy() {
        // Remove event listeners
        if (this.options && this.options.removable) {
            const removeBtn = this.element.querySelector('.mpz-badge__remove');
            if (removeBtn) {
                removeBtn.removeEventListener('click', this.handleRemove);
            }
        }

        // Remove from DOM
        if (this.element && this.element.parentNode) {
            this.element.parentNode.removeChild(this.element);
        }

        this.element = null;
        this.options = null;
    }

    /**
     * Create badge from status code
     * Convenience method for common translation statuses
     *
     * @param {string} status - Status code: 'translated', 'pending', 'draft', 'failed', 'original', 'needs_update', 'active'
     * @param {Object} options - Additional options to override defaults
     * @return {Badge} Badge instance
     */
    static fromStatus(status, options = {}) {
        const statusMap = {
            'translated': {
                text: 'Translated',
                variant: 'success',
                icon: 'dashicons-yes-alt',
                tooltip: 'Translation completed'
            },
            'active': {
                text: 'Active',
                variant: 'success',
                icon: 'dashicons-yes',
                tooltip: 'Currently active'
            },
            'needs_update': {
                text: 'Needs Update',
                variant: 'warning',
                icon: 'dashicons-update',
                tooltip: 'Original content has changed'
            },
            'pending': {
                text: 'Pending',
                variant: 'warning',
                icon: 'dashicons-clock',
                tooltip: 'Translation in progress'
            },
            'failed': {
                text: 'Failed',
                variant: 'danger',
                icon: 'dashicons-dismiss',
                tooltip: 'Translation failed'
            },
            'error': {
                text: 'Error',
                variant: 'danger',
                icon: 'dashicons-warning',
                tooltip: 'An error occurred'
            },
            'draft': {
                text: 'Draft',
                variant: 'info',
                icon: 'dashicons-edit',
                tooltip: 'Draft translation'
            },
            'original': {
                text: 'Original',
                variant: 'default',
                icon: null,
                tooltip: 'Original content'
            }
        };

        const statusConfig = statusMap[status] || statusMap['original'];

        // Merge status defaults with provided options
        const mergedOptions = {
            ...statusConfig,
            ...options
        };

        return new Badge(mergedOptions);
    }
}

// Export for module usage
export default Badge;
